import { Appearance, ContactOption, FieldOption, StripeAddressElementOptions, StripePaymentElementOptions } from '@stripe/stripe-js';
import { BillingDetails, PaymentMethodType } from '@aiwyn/api';
import { SemanticTokenName, WhiteLabelService } from '@aiwyn/shared';

// TODO :: One day, update these
const RED_300 = '#BF371C';
const RED_800 = '#FFB5A6';
const RED_1100 = '#FFF6F5';
const UNSET = 'unset';

export function getWhiteLabeledStripeElementAppearance(): Appearance {
  // Primitives
  const primaryColor = WhiteLabelService.getComputedVariableValue(SemanticTokenName.COLOR_PRIMARY_500);

  // Semantic tokens
  const borderColorPrimarySubtle = WhiteLabelService.getComputedVariableValue(SemanticTokenName.BORDER_COLOR_PRIMARY_SUBTLE);
  const borderColorPrimarySubtleHover = WhiteLabelService.getComputedVariableValue(SemanticTokenName.BORDER_COLOR_PRIMARY_SUBTLE_HOVER);
  const borderColorPrimarySubtleSelected = WhiteLabelService.getComputedVariableValue(SemanticTokenName.BORDER_COLOR_PRIMARY_SUBTLE_SELECTED);

  const textColorPrimaryBase = WhiteLabelService.getComputedVariableValue(SemanticTokenName.TEXT_COLOR_PRIMARY_BASE);
  // const textColorPrimaryBaseSelected = WhiteLabelService.getComputedVariableValue(SemanticTokenName.TEXT_COLOR_PRIMARY_BASE_SELECTED);
  const textColorPrimaryBaseHover = WhiteLabelService.getComputedVariableValue(SemanticTokenName.TEXT_COLOR_PRIMARY_BASE_HOVER);

  // Neutrals do not yet have semantic tokens, so we use the raw CSS variables
  const neutral100 = WhiteLabelService.getComputedVariableValue(SemanticTokenName.COLOR_NEUTRAL_100);
  const neutral200 = WhiteLabelService.getComputedVariableValue(SemanticTokenName.COLOR_NEUTRAL_200);
  const neutral600 = WhiteLabelService.getComputedVariableValue(SemanticTokenName.COLOR_NEUTRAL_600);
  const neutral1100 = WhiteLabelService.getComputedVariableValue(SemanticTokenName.COLOR_NEUTRAL_1100);

  return {
    theme: 'stripe',
    variables: {
      spacingUnit: '4px',
      borderRadius: '8px',
      fontSizeXs: '15px',
      colorPrimary: primaryColor,
      colorTextPlaceholder: neutral600,
    },
    rules: {
      /**
       * Both elements
       */
      '.Label': {
        fontSize: '14px',
        lineHeight: '20px',
        marginTop: '4px',
        marginBottom: '8px',
      },
      '.Error': {
        color: RED_300,
        fontSize: '12px',
        fontWeight: '300',
        lineHeight: '16px',
        marginTop: '8px',
      },
      '.Input': {
        borderRadius: '4px',
        color: neutral200,
        fontSize: '14px',
        fontWeight: '400',
        padding: '6px 12px',
        // TODO REFACTOR: This is just to stop inputs from scrolling
        lineHeight: '14px',
      },
      '.Input--invalid': {
        color: RED_300,
        backgroundColor: RED_1100,
        borderColor: RED_800,
        boxShadow: UNSET,
      },

      /**
       * Payment element
       */
      '.AccordionItem': {
        borderColor: borderColorPrimarySubtle,
        color: textColorPrimaryBase,
      },
      '.AccordionItem:hover': {
        borderColor: borderColorPrimarySubtleHover,
        color: textColorPrimaryBaseHover,
      },
      '.AccordionItem--selected': {
        backgroundColor: neutral1100,
        border: `1.5px solid ${borderColorPrimarySubtleSelected}`,
        color: neutral100,
      },
      // duplicating here to override hover state of AccordionItem
      '.AccordionItem--selected:hover': {
        backgroundColor: neutral1100,
        borderColor: borderColorPrimarySubtleHover,
        color: neutral100,
      },
      '.Link': {
        textDecoration: 'underline',
      },
      // notice how fill is applied but not height/width
      // '.TabIcon': {
      //   height: '16px',
      //   width: '16px',
      //   fill: 'purple',
      // },

      /**
       * Address element
       */
      '.PickerItem': {
        borderColor: borderColorPrimarySubtle,
        color: textColorPrimaryBase,
        transition: UNSET, // payment element doesn't have a transition
      },
      '.PickerItem:hover': {
        borderColor: borderColorPrimarySubtleHover,
        backgroundColor: UNSET,
        color: textColorPrimaryBaseHover,
      },
      '.PickerItem--highlight': {
        boxShadow: UNSET,
        color: neutral100,
        backgroundColor: neutral1100,
      },
      '.PickerItem--highlight:hover': {
        boxShadow: UNSET,
        color: neutral100,
        backgroundColor: neutral1100,
      },
      '.PickerItem--selected': {
        color: neutral100,
      },
      '.PickerItem--selected:hover': {
        color: neutral100,
      },
      '.PickerItem--new:hover': {
        color: textColorPrimaryBaseHover,
      },
      // NOT VALID
      // '.p-SomePrivateClass': {...} // only public class names can be targeted
      // '.Tab .TabLabel': {...} // ancestor-descendant relationships in selectors are unsupported
      // '.Tab--invalid': {...} // the .Tab class does not support the --invalid state
    },
  };
}

export interface StripElementsInitializationData {
  setupIntentClientSecret: string;
  newPaymentMethodType: PaymentMethodType;
  newBillingDetails: BillingDetails;
}

export const STRIPE_DEFAULT_PAYMENT_ELEMENT_OPTIONS: StripePaymentElementOptions = {
  layout: {
    defaultCollapsed: true,
    radios: false,
    spacedAccordionItems: true,
    type: 'accordion',
  },
  paymentMethodOrder: ['apple_pay', 'google_pay', 'card', 'us_bank_account', 'acss_debit', 'bacs_debit'],
};

export interface StripePaymentElementCustomOptions {
  defaultCollapsed?: boolean;
  paymentMethodOrder?: string[];
  readOnly?: boolean;
  email?: FieldOption;
  address?: FieldOption;
}

export const STRIPE_DEFAULT_ADDRESS_ELEMENT_OPTIONS: StripeAddressElementOptions = {
  mode: 'billing',
  fields: {
    phone: 'never',
    // email: 'never',
  },
};

export interface StripeAddressElementCustomOptions {
  allowedCountries?: string[];
  contacts?: ContactOption[];
  defaultValues?: typeof STRIPE_DEFAULT_ADDRESS_ELEMENT_OPTIONS.defaultValues;
}
